import React from 'react';
import { QueryClient } from 'react-query';

import { log } from '@24i/nxg-core-utils/src/logger';
import { ASSET_TYPE, QUERY_KEYS } from '@24i/nxg-sdk-photon';
import type { Asset, AdditionalItems, Channel, Broadcast, PageSection } from '@24i/nxg-sdk-photon';

import type {
    TimestampRecord,
    ExtendedTimestampRecord,
    ThrottlingOptions,
    RefetchIntervalResult,
    ProcessTimestampsResult,
    TrackDataChangesResult,
    PendingAssetInvalidation,
    BackoffOptions,
    FetchPlaylistFn,
    ProcessTimestampsParams,
    IntervalReuseDebugInfo,
} from './types';
import {
    BUFFER_TIME_MS,
    SOON_THRESHOLD_MS,
    MIN_INVALIDATION_INTERVAL,
    INTERVAL_REUSE_MAX_AGE,
    BACKOFF_TIMES,
    DEFAULT_REFETCH_INTERVAL,
    MAX_TIMESTAMP_CALCULATIONS_PER_INTERVAL,
    MAX_THROTTLED_INTERVAL,
    MIN_THROTTLED_INTERVAL,
    UNCHANGED_FETCHES_BEFORE_THROTTLING,
    CONSECUTIVE_FETCH_LOG_INTERVAL,
    THROTTLING_LOG_INTERVAL,
    REFRESH_LOG_MIN_INTERVAL_MS,
    REUSE_LOG_MIN_INTERVAL_MS,
    RECENT_PAST_THRESHOLD_MS,
    QUICK_REFRESH_MAX_MS,
    DEBUG,
    LOG_PREFIX,
} from './constants';

// Helper function for logging with timestamp
export const debugLog = (...args: unknown[]): void => {
    if (DEBUG) {
        const now = new Date();
        const timestamp = `[${now.toLocaleTimeString('en-US', { hour12: false })}]`;
        log(`${LOG_PREFIX} ${timestamp}`, ...args);
    }
};

// Helper function to format milliseconds into readable time
export const msToTime = (ms: number): string => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    if (ms < 3600000) return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
    return `${Math.floor(ms / 3600000)}h ${Math.floor((ms % 3600000) / 60000)}m ${Math.floor(
        (ms % 60000) / 1000
    )}s`;
};

// Helper for updating future timestamp data
const updateFutureTimestamp = (
    timestamps: ExtendedTimestampRecord,
    endsAt: number,
    assetId: string,
    assetType: ASSET_TYPE,
    channelId: string | undefined,
    playlistId: string | null | undefined
): ExtendedTimestampRecord => {
    const updatedTimestamps = { ...timestamps };

    // Only update if this is the earliest future timestamp
    if (!updatedTimestamps.future || endsAt < updatedTimestamps.future) {
        updatedTimestamps.future = endsAt;
        updatedTimestamps.futurePlaylistId = playlistId;
        updatedTimestamps.futureChannelId = channelId;

        // Track for logging
        updatedTimestamps.futureAssetIds.push(assetId);
        updatedTimestamps.futureAssetTypes.push(assetType);
        updatedTimestamps.futurePlaylistIds.push(playlistId);

        // Log if debugging is enabled
        if (DEBUG) {
            const timeUntil = new Date(endsAt * 1000 - Date.now());

            debugLog(
                `📅 Future ${assetType} ${assetId} ends in ${timeUntil.getMinutes()}m ${timeUntil.getSeconds()}s`
            );
        }
    }

    return updatedTimestamps;
};

// Helper for updating past timestamp data
const updatePastTimestamp = (
    timestamps: ExtendedTimestampRecord,
    endsAt: number,
    assetId: string,
    assetType: ASSET_TYPE,
    channelId: string | undefined,
    playlistId: string | null | undefined
): ExtendedTimestampRecord => {
    const updatedTimestamps = { ...timestamps };

    // Only update if this is the earliest past timestamp
    if (!updatedTimestamps.past || endsAt < updatedTimestamps.past) {
        updatedTimestamps.past = endsAt;
        updatedTimestamps.pastPlaylistId = playlistId;
        updatedTimestamps.pastChannelId = channelId;

        // Track for logging
        updatedTimestamps.pastAssetIds.push(assetId);
        updatedTimestamps.pastAssetTypes.push(assetType);
        updatedTimestamps.pastPlaylistIds.push(playlistId);

        // Log if debugging is enabled
        if (DEBUG) {
            const timeSince = new Date(Date.now() - endsAt * 1000);

            debugLog(
                `⏲️ Past ${assetType} ${assetId} ended ${timeSince.getMinutes()}m ${timeSince.getSeconds()}s ago`
            );
        }
    }

    return updatedTimestamps;
};

// Process channel item helper function
const processChannelAsset = (
    channelItem: Channel,
    currentTimestamp: number,
    timestamps: ExtendedTimestampRecord,
    playlistId: string | null | undefined
): ExtendedTimestampRecord => {
    if (!channelItem.endsAt) return timestamps;

    const updatedTimestamps = { ...timestamps };

    if (channelItem.endsAt > currentTimestamp) {
        // Future timestamp
        return updateFutureTimestamp(
            updatedTimestamps,
            channelItem.endsAt,
            channelItem.id,
            ASSET_TYPE.CHANNEL,
            channelItem.id,
            playlistId
        );
    }

    // Past timestamp
    return updatePastTimestamp(
        updatedTimestamps,
        channelItem.endsAt,
        channelItem.id,
        ASSET_TYPE.CHANNEL,
        channelItem.id,
        playlistId
    );
};

// Process broadcast or live event asset helper function
const processBroadcastOrLiveEvent = (
    asset: Asset,
    itemType: ASSET_TYPE,
    currentTimestamp: number,
    timestamps: ExtendedTimestampRecord,
    playlistId: string | null | undefined
): ExtendedTimestampRecord => {
    if (!asset.endsAt) return timestamps;

    const updatedTimestamps = { ...timestamps };

    if (asset.endsAt > currentTimestamp) {
        // Future timestamp
        return updateFutureTimestamp(
            updatedTimestamps,
            asset.endsAt,
            asset.id,
            itemType,
            asset.channelId ?? undefined,
            playlistId
        );
    }

    // Past timestamp
    return updatePastTimestamp(
        updatedTimestamps,
        asset.endsAt,
        asset.id,
        itemType,
        asset.channelId ?? undefined,
        playlistId
    );
};

// Main function to process an item and update lowest timestamps
export const processChannelItem = (
    item: Asset | AdditionalItems,
    currentTimestamp: number,
    lowestTimestamps: ExtendedTimestampRecord,
    playlistId: string | null | undefined
): ExtendedTimestampRecord => {
    // Process channel items
    if (item.type === ASSET_TYPE.CHANNEL) {
        return processChannelAsset(item as Channel, currentTimestamp, lowestTimestamps, playlistId);
    }

    // Process broadcast or live event assets
    if (item.type === ASSET_TYPE.BROADCAST || item.type === ASSET_TYPE.LIVE_EVENT) {
        return processBroadcastOrLiveEvent(
            item as Asset,
            item.type,
            currentTimestamp,
            lowestTimestamps,
            playlistId
        );
    }

    return lowestTimestamps;
};

/**
 * Calculate milliseconds until the next program start time
 * @param futureTimestamp - Unix timestamp of future program
 * @param now - Current date for time calculations
 * @returns Milliseconds until next program or false if no future program
 */
export const calculateMillisecondsUntilNextProgram = (
    futureTimestamp: number | undefined,
    now: Date
): number | false => {
    if (!futureTimestamp) {
        return false;
    }

    const nextProgramDate = new Date(futureTimestamp * 1000);
    return nextProgramDate.getTime() - now.getTime();
};

/**
 * Generic backoff calculator with configurable parameters
 * This unified backoff system can be used for both:
 * 1. Array-based backoff (using BACKOFF_TIMES for main query intervals)
 * 2. Formula-based backoff (for throttling calculations and invalidation operations)
 *
 * This unified approach ensures consistent backoff behavior throughout the codebase.
 *
 * @param count - Current count (attempts, unchanged fetches, etc.)
 * @param options - Configuration options for backoff calculation
 * @returns Time in milliseconds for next backoff
 */
export const calculateGenericBackoff = (count: number, options: BackoffOptions = {}): number => {
    const {
        useArray = true,
        baseIntervalMs = MIN_INVALIDATION_INTERVAL,
        maxMultiplier = 5,
        stepSize = 20,
        arrayValues = BACKOFF_TIMES,
    } = options;

    // Array-based backoff (original method for main backoff)
    if (useArray && arrayValues.length > 0) {
        const backoffIndex = Math.min(count, arrayValues.length - 1);
        return arrayValues[backoffIndex] * 1000; // convert to ms
    }

    // Formula-based backoff (similar to channel invalidation throttling)
    const multiplier = 1 + Math.min(maxMultiplier, Math.floor(count / stepSize));
    return baseIntervalMs * multiplier;
};

/**
 * Calculate backoff time in milliseconds based on backoff count
 * Uses the predefined BACKOFF_TIMES array
 * @param backoffCount - Current backoff attempt count
 * @returns Time in milliseconds for next backoff
 */
export const calculateBackoffTimeMs = (backoffCount: number): number =>
    calculateGenericBackoff(backoffCount, {
        useArray: true,
        arrayValues: BACKOFF_TIMES,
    });

/**
 * Extract lowest timestamps from data
 * @param data - The query data to process
 * @returns Record containing lowest past and future timestamps
 */
export const findLowestTimestamps = (
    data: { pages?: PageSection[][] } | undefined,
    calculationCountRef?: React.MutableRefObject<number>,
    lastCalculationTimeRef?: React.MutableRefObject<number>
): TimestampRecord => {
    // Update calculation metrics if provided
    const updateCalculationCount = calculationCountRef
        ? () => {
              // eslint-disable-next-line no-param-reassign
              calculationCountRef.current += 1;
          }
        : () => {};
    updateCalculationCount();

    const updateCalculationTime = lastCalculationTimeRef
        ? () => {
              // eslint-disable-next-line no-param-reassign
              lastCalculationTimeRef.current = Date.now();
          }
        : () => {};
    updateCalculationTime();

    // Extended timestamp record with additional tracking for assets
    let lowestTimestamps: TimestampRecord & {
        pastAssetIds: string[];
        pastAssetTypes: ASSET_TYPE[];
        pastPlaylistIds: (string | null | undefined)[];
        futureAssetIds: string[];
        futureAssetTypes: ASSET_TYPE[];
        futurePlaylistIds: (string | null | undefined)[];
    } = {
        past: undefined,
        future: undefined,
        pastPlaylistId: undefined,
        futurePlaylistId: undefined,
        pastChannelId: undefined,
        futureChannelId: undefined,
        // Additional tracking arrays
        pastAssetIds: [],
        pastAssetTypes: [],
        pastPlaylistIds: [],
        futureAssetIds: [],
        futureAssetTypes: [],
        futurePlaylistIds: [],
    };

    const now = new Date();
    const currentTimestamp = Math.floor(now.getTime() / 1000);

    // Process all data to find channel timestamps
    data?.pages?.forEach((page: PageSection[]) => {
        page?.forEach((section: PageSection) => {
            const {
                playlistId,
                items = [],
                additionalItems = [],
                computedPlaylist,
                _links: links,
            } = section;

            // For computed playlists, use the path from links.href to match what's used as the query key
            // This ensures we're invalidating the correct query
            let usedPlaylistId: string | null | undefined = playlistId;

            if (!usedPlaylistId && computedPlaylist && links?.href) {
                // Extract the path part from href, similar to formatComputedPlaylistAssetKey
                const params = links.href.split('playlists/');
                usedPlaylistId = params?.[1] || computedPlaylist;
            }

            // Process both items and additionalItems in the section
            items?.forEach((item) => {
                lowestTimestamps = processChannelItem(
                    item,
                    currentTimestamp,
                    lowestTimestamps,
                    usedPlaylistId
                );
            });

            additionalItems?.forEach((item) => {
                lowestTimestamps = processChannelItem(
                    item,
                    currentTimestamp,
                    lowestTimestamps,
                    usedPlaylistId
                );
            });
        });
    });

    return lowestTimestamps;
};

/**
 * Handles the case when we have very recent past content
 * Prioritizes quick refresh for recently ended broadcasts
 */
const handleVeryRecentPastContent = (
    lowestTimestamps: TimestampRecord,
    backoffTimeMs: number,
    msUntilNextProgram: number | false,
    now: Date
): RefetchIntervalResult => {
    // Use a quick refresh time for recently ended broadcasts (max 10 seconds)
    const quickInterval = Math.min(QUICK_REFRESH_MAX_MS, backoffTimeMs);

    if (DEBUG && lowestTimestamps.past !== undefined) {
        const pastMinutesAgo = Math.round(
            (now.getTime() - lowestTimestamps.past * 1000) / (60 * 1000)
        );
        const futureInfo =
            msUntilNextProgram !== false
                ? `instead of waiting ${msUntilNextProgram / 1000}s for future broadcast`
                : '';

        debugLog(
            `⚡ Found very recent past broadcast (${pastMinutesAgo}m ago) - prioritizing quick refresh in ${
                quickInterval / 1000
            }s ${futureInfo}`
        );
    }

    return {
        interval: quickInterval,
        usedFutureTime: false, // Mark as past-focused to reflect urgency
    };
};

/**
 * Handles the case when we have both past and future content
 */
const handlePastAndFutureContent = (
    msUntilNextProgram: number,
    backoffTimeMs: number
): RefetchIntervalResult => {
    const nextProgramTimeWithBuffer = msUntilNextProgram + BUFFER_TIME_MS;

    // For older past content, compare backoff time vs future time - use the GREATER of the two
    // This ensures we don't refetch too soon when there's future content
    // but also ensures we don't wait too long for stale content
    const useInterval =
        nextProgramTimeWithBuffer <= SOON_THRESHOLD_MS
            ? nextProgramTimeWithBuffer // Use exact future time when it's coming soon
            : Math.max(backoffTimeMs, nextProgramTimeWithBuffer); // Otherwise use whichever is greater

    return {
        interval: useInterval,
        usedFutureTime: true,
    };
};

/**
 * Calculates optimal refetch interval based on channel program end times
 * Uses different strategies depending on whether content is past or future
 * Exported for unit testing purposes
 *
 * @param data - Query data containing playlist sections with timing information
 * @param backoffCount - Current backoff count for progressive backoff strategy
 * @param existingTimestamps - Optional pre-calculated timestamps for optimization
 * @returns Object with interval (in ms or false to disable) and timing strategy used
 */
export const calculateRefetchInterval = (
    data: { pages?: PageSection[][] } | undefined,
    backoffCount = 0,
    existingTimestamps?: TimestampRecord
): RefetchIntervalResult => {
    // Step 1: Find the lowest timestamps in past and future
    const now = new Date();
    const lowestTimestamps = existingTimestamps || findLowestTimestamps(data);

    // Step 2: Calculate times for both scenarios
    const hasPastContent = lowestTimestamps.past !== undefined;
    const hasFutureContent = lowestTimestamps.future !== undefined;

    // Calculate time until next program (if exists)
    const msUntilNextProgram = hasFutureContent
        ? calculateMillisecondsUntilNextProgram(lowestTimestamps.future, now)
        : false;

    // Calculate backoff time for past content
    const backoffTimeMs = hasPastContent ? calculateBackoffTimeMs(backoffCount) : 0;

    // Step 3: Determine refetch interval based on content types

    // Case 1: We have past content (stale)
    if (hasPastContent) {
        // Check if the past content is very recent (within the threshold)
        // This is critical for handling the case where we have just loaded stale data
        const isVeryRecentPast =
            lowestTimestamps.past &&
            now.getTime() - lowestTimestamps.past * 1000 <= RECENT_PAST_THRESHOLD_MS;

        // Handle very recent past content as a priority case regardless of future content
        if (isVeryRecentPast) {
            return handleVeryRecentPastContent(
                lowestTimestamps,
                backoffTimeMs,
                msUntilNextProgram,
                now
            );
        }

        // Case 1A: We also have future content scheduled
        if (msUntilNextProgram !== false) {
            return handlePastAndFutureContent(msUntilNextProgram, backoffTimeMs);
        }

        // Case 1B: Only past content, use backoff strategy
        return {
            interval: backoffTimeMs,
            usedFutureTime: false,
        };
    }

    // Case 2: We only have future content (no stale content)
    if (hasFutureContent && msUntilNextProgram !== false) {
        // Schedule update for next program time with buffer
        return {
            interval: msUntilNextProgram + BUFFER_TIME_MS,
            usedFutureTime: true,
        };
    }

    // Case 3: No program times found at all
    return {
        interval: false,
        usedFutureTime: false,
    };
};

export const getSectionId = (section: PageSection, index: number): string => {
    const { id, playlistId, computedPlaylist, label } = section;
    return id || playlistId || computedPlaylist || label || `section-${index}`;
};

export const getSectionsStructureHash = (sectionsStructure: PageSection[]): string =>
    sectionsStructure.map(getSectionId).join('|');

/**
 * Applies throttling to refetch intervals based on consecutive unchanged fetches
 * Preserves future timestamps but throttles past timestamp fetches
 * @param interval - Base interval to potentially throttle (in milliseconds)
 * @param usedFutureTime - Whether we're using a future timestamp
 * @param consecutiveUnchangedFetches - Count of consecutive fetches with no data changes
 * @param options - Optional configuration to override default throttling behavior
 * @returns Potentially throttled interval (in milliseconds)
 */
export const applyThrottling = (
    interval: number,
    usedFutureTime: boolean,
    consecutiveUnchangedFetches: number,
    options?: ThrottlingOptions
): number => {
    const {
        minInterval = MIN_THROTTLED_INTERVAL,
        maxInterval = MAX_THROTTLED_INTERVAL,
        unchangedThreshold = UNCHANGED_FETCHES_BEFORE_THROTTLING,
        logFrequency = THROTTLING_LOG_INTERVAL,
    } = options || {};

    // Only apply throttling after threshold is reached
    if (consecutiveUnchangedFetches < unchangedThreshold) {
        return interval;
    }

    // For future timestamps, preserve the original interval
    if (usedFutureTime) {
        if (consecutiveUnchangedFetches % logFrequency === 0) {
            debugLog(
                `🐢 Preserving future timestamp interval of ${msToTime(
                    interval
                )} despite ${consecutiveUnchangedFetches} unchanged fetches`
            );
        }
        return interval;
    }

    // For past timestamps (backoff mode), apply throttling using our unified approach
    const originalInterval = interval;

    // Calculate throttled interval using our unified backoff function with custom parameters
    const baseThrottledInterval = calculateGenericBackoff(consecutiveUnchangedFetches, {
        useArray: false,
        baseIntervalMs: interval,
        maxMultiplier: 20,
        stepSize: logFrequency,
    });

    // Apply min/max constraints
    const throttledInterval = Math.min(Math.max(baseThrottledInterval, minInterval), maxInterval);

    if (consecutiveUnchangedFetches % logFrequency === 0) {
        // Log only occasionally to avoid log spam
        // Calculate the effective factor for logging
        const effectiveFactor = throttledInterval / originalInterval;

        debugLog(
            `🐢 Throttling: ${consecutiveUnchangedFetches} unchanged fetches. ` +
                `Original: ${msToTime(originalInterval)}, New: ${msToTime(throttledInterval)}, ` +
                `Factor: ${effectiveFactor.toFixed(1)}x`
        );
    }

    return throttledInterval;
};

/**
 * Updates backoff counter based on calculation results
 * Resets counter if using future timestamps or no past timestamps exist
 * Increments counter when using past timestamps for backoff
 */
export const updateBackoffCounter = (
    backoffCountRef: React.MutableRefObject<number>,
    pastTimestamp: number | undefined,
    usedFutureTime: boolean,
    hasNumericInterval: boolean
): void => {
    // Reset backoff counter if no items are in the past OR we're using future time as interval
    if (pastTimestamp === undefined || usedFutureTime) {
        if (backoffCountRef.current > 0) {
            debugLog('⏱️ Resetting backoff counter from', backoffCountRef.current, 'to 0');
        }
        // eslint-disable-next-line no-param-reassign
        backoffCountRef.current = 0;
    }
    // Otherwise, increment backoff counter if we're using backoff times and have past timestamps
    else if (hasNumericInterval && pastTimestamp !== undefined) {
        const oldBackoffCount = backoffCountRef.current;
        // eslint-disable-next-line no-param-reassign
        backoffCountRef.current = Math.min(backoffCountRef.current + 1, BACKOFF_TIMES.length - 1);

        if (oldBackoffCount !== backoffCountRef.current) {
            debugLog(
                `⏱️ Incremented backoff from ${oldBackoffCount} to ${backoffCountRef.current} (${
                    BACKOFF_TIMES[backoffCountRef.current]
                }s interval)`
            );
        }
    }
};

// Function to generate a simple hash from the query data to detect changes
export const generateDataHash = (data: { pages?: PageSection[][] }): string => {
    if (!data || !data.pages) return 'empty';
    if (data.pages.length === 0) return 'empty-pages';

    // Create a simplified representation of the data structure to track meaningful changes
    const simplifiedData = data.pages.map((page: PageSection[]) =>
        page.map((section: PageSection) => {
            // Extract only the relevant information for change detection
            const items = (section.items || []).map((item) => ({
                id: item.id,
                type: item.type,
                title: item.title, // Include title for better differentiation
                endsAt:
                    item.type === ASSET_TYPE.BROADCAST ||
                    item.type === ASSET_TYPE.CHANNEL ||
                    item.type === ASSET_TYPE.LIVE_EVENT
                        ? (item as Broadcast | Channel).endsAt
                        : undefined,
            }));

            return {
                playlistId: section.playlistId,
                computedPlaylist: section.computedPlaylist,
                itemCount: items.length,
                items,
            };
        })
    );

    try {
        // Use a stringified version for quick comparison
        return JSON.stringify(simplifiedData);
    } catch (e: unknown) {
        debugLog('Error generating data hash:', e);
        return Date.now().toString(); // Fallback to timestamp if JSON stringify fails
    }
};

/**
 * Fetches playlist data from API and filters out empty sections
 */
export const fetchAndFilterPlaylists = async (
    fetchPlaylist: FetchPlaylistFn,
    sectionsStructure: PageSection[],
    fetchSize: number,
    pageParam: number
): Promise<PageSection[]> => {
    const startTime = Date.now();
    debugLog(`📥 Fetching playlist data from API`);
    const sections = await fetchPlaylist(sectionsStructure, fetchSize, pageParam);
    const fetchTime = Date.now() - startTime;

    debugLog(`✅ Fetch complete in ${msToTime(fetchTime)}: ${sections.length} sections`);

    // Filter out empty sections (no items and no additionalItems)
    return sections.filter(
        (section) =>
            (section.items && section.items.length > 0) ||
            (section.additionalItems && section.additionalItems.length > 0)
    );
};

/**
 * Queues invalidation requests for playlists and channels for the next fetch cycle
 * Applies throttling for repeated invalidations and extends refetch interval when appropriate
 * @param playlistId The playlist ID that triggered the refetch
 * @param channelId The channel ID that triggered the refetch
 * @param consecutiveUnchangedFetches Number of consecutive fetches without data changes
 * @param pendingPlaylistInvalidations Collection of playlist IDs to be invalidated
 * @param pendingChannelInvalidations Collection of channel IDs to be invalidated
 * @param lastInvalidationTimeRef Tracking for last invalidation time per resource
 * @param interval Current calculated interval
 * @returns The potentially extended interval after processing
 */
export const processInvalidations = (
    playlistId: string | null | undefined,
    channelId: string | null | undefined,
    consecutiveUnchangedFetches: number,
    pendingPlaylistInvalidations: React.MutableRefObject<Set<string>>,
    pendingChannelInvalidations: React.MutableRefObject<Set<string>>,
    lastInvalidationTimeRef: React.MutableRefObject<Record<string, number>>,
    interval: number
): number => {
    const now = Date.now();

    // Queue invalidations instead of immediately performing them
    if (playlistId) {
        // Check if this playlist was recently invalidated to avoid duplicates
        const invalidationKey = `playlist:${playlistId}`;
        const lastInvalidationTime = lastInvalidationTimeRef.current[invalidationKey] || 0;
        const timeSinceLastInvalidation = now - lastInvalidationTime;

        if (timeSinceLastInvalidation > MIN_INVALIDATION_INTERVAL) {
            // Add playlist to pending invalidations queue if not too recent
            pendingPlaylistInvalidations.current.add(playlistId);
            debugLog(`🔍 Queuing playlist invalidation for next fetch: ${playlistId}`);
        } else {
            debugLog(
                `⏭️ Skipping duplicate playlist invalidation for ${playlistId} (last invalidated ${msToTime(
                    timeSinceLastInvalidation
                )} ago)`
            );
        }
    }

    // If we have a channelId, queue channel invalidation
    if (channelId) {
        // Track if all invalidations are throttled to potentially extend the refetch interval
        let allInvalidationsThrottled = true;

        // Check if this channel was recently invalidated to avoid duplicates
        const channelInvalidationKey = `channel:${channelId}`;
        const lastChannelInvalidation =
            lastInvalidationTimeRef.current[channelInvalidationKey] || 0;
        const timeSinceLastChannelInvalidation = now - lastChannelInvalidation;

        // Get the dynamic throttle interval using our unified backoff calculation
        const channelThrottleInterval = calculateGenericBackoff(consecutiveUnchangedFetches, {
            useArray: false,
            baseIntervalMs: MIN_INVALIDATION_INTERVAL,
            maxMultiplier: 5,
            stepSize: 20,
        });

        if (timeSinceLastChannelInvalidation > channelThrottleInterval) {
            // Add channel to pending invalidations queue
            pendingChannelInvalidations.current.add(channelId);
            debugLog(`🔍 Queuing channel invalidation for next fetch: ${channelId}`);
            allInvalidationsThrottled = false;
        } else {
            debugLog(
                `⏭️ Skipping duplicate channel invalidation for ${channelId} (last invalidated ${msToTime(
                    timeSinceLastChannelInvalidation
                )} ago)`
            );
        }

        // If all invalidations were throttled and we've had many unchanged fetches,
        // dramatically increase the refetch interval to avoid wasted API calls
        if (
            allInvalidationsThrottled &&
            consecutiveUnchangedFetches > UNCHANGED_FETCHES_BEFORE_THROTTLING
        ) {
            const originalInterval = interval;
            // Use our consolidated throttling function with default options for consistent behavior
            const extendedInterval = applyThrottling(
                originalInterval,
                false, // Not using future timestamp
                consecutiveUnchangedFetches
            );

            if (DEBUG && consecutiveUnchangedFetches % THROTTLING_LOG_INTERVAL === 0) {
                debugLog(
                    `⏳ All invalidations throttled - extending refetch interval: ${msToTime(
                        originalInterval
                    )} → ${msToTime(extendedInterval)} (${(
                        extendedInterval / originalInterval
                    ).toFixed(1)}x)`
                );
            }

            return extendedInterval;
        }
    }

    // Return original interval if no changes were made
    return interval;
};

/**
 * Processes all pending invalidations that were queued during previous refetch cycles
 * Invalidates cached queries for broadcasts, channels, playlists and computed playlists
 * Batches invalidation operations for improved performance
 */
export const processPendingInvalidations = async (
    sectionsStructure: PageSection[],
    queryClient: QueryClient,
    pendingAssetInvalidationsRef: React.MutableRefObject<PendingAssetInvalidation[]>,
    pendingPlaylistInvalidationsRef: React.MutableRefObject<Set<string>>,
    pendingChannelInvalidationsRef: React.MutableRefObject<Set<string>>,
    lastInvalidationTimeRef: React.MutableRefObject<Record<string, number>>
): Promise<void> => {
    const now = Date.now();
    const hasInvalidations =
        pendingAssetInvalidationsRef.current.length > 0 ||
        pendingPlaylistInvalidationsRef.current.size > 0 ||
        pendingChannelInvalidationsRef.current.size > 0;

    if (!hasInvalidations) {
        return;
    }

    // Collect all stale items from the invalidation queues
    const staleBroadcastIds = pendingAssetInvalidationsRef.current
        .filter((item) => item.assetType === ASSET_TYPE.BROADCAST)
        .map((item) => item.assetId)
        .filter(Boolean);

    const staleChannelIds = [...pendingChannelInvalidationsRef.current].concat(
        pendingAssetInvalidationsRef.current
            .filter((item) => item.assetType === ASSET_TYPE.CHANNEL)
            .map((item) => item.assetId)
            .filter(Boolean)
    );

    const stalePlaylistIds = [...pendingPlaylistInvalidationsRef.current];
    // Initialize empty array for computed playlists (now we only get them from sections)
    const staleComputedPlaylistIds: string[] = [];

    // Add computed playlists that are present in the current sections structure
    // This ensures we only invalidate computed playlists that are actually displayed
    sectionsStructure.forEach((section) => {
        if (section.computedPlaylist && !stalePlaylistIds.includes(section.computedPlaylist)) {
            // Only add computed playlists that aren't already in the regular playlist list
            staleComputedPlaylistIds.push(section.computedPlaylist);
        }
    });

    // Deduplicate IDs
    const uniqueChannelIds = [...new Set(staleChannelIds)];
    const uniquePlaylistIds = [...new Set(stalePlaylistIds)];
    const uniqueComputedPlaylistIds = [...new Set(staleComputedPlaylistIds)];

    // Find playlists that are counted both as regular and computed playlists
    const duplicatePlaylists = uniquePlaylistIds.filter((id) =>
        uniqueComputedPlaylistIds.includes(id)
    );

    // Create a more informative log message
    let logMessage = [
        '🧹 Processing invalidation queue:',
        `${staleBroadcastIds.length} broadcasts,`,
        `${uniqueChannelIds.length} channels,`,
        `${uniquePlaylistIds.length} playlists,`,
        `${uniqueComputedPlaylistIds.length} computed playlists`,
    ].join(' ');

    // Add details about duplicate playlists if any exist
    if (duplicatePlaylists.length > 0) {
        logMessage += ` (Note: ${duplicatePlaylists.length} playlist${
            duplicatePlaylists.length > 1 ? 's' : ''
        } counted in both categories: ${duplicatePlaylists.join(', ')})`;
    }

    debugLog(logMessage);

    // Batch all invalidation promises for better performance
    const invalidationPromises: Promise<unknown>[] = [];

    // Helper function to track invalidation time (for logging purposes only)
    const trackInvalidation = (key: string): void => {
        // eslint-disable-next-line no-param-reassign
        lastInvalidationTimeRef.current[key] = now;
        debugLog(`⏱️ Tracking invalidation time for ${key}`);
    };

    // First invalidate specific media items (broadcasts)
    if (staleBroadcastIds.length > 0) {
        debugLog(`🔄 Invalidating ${staleBroadcastIds.length} specific broadcasts`);

        staleBroadcastIds.forEach((id) => {
            if (!id) return;

            const invalidationKey = `broadcast:${id}`;
            trackInvalidation(invalidationKey);
            invalidationPromises.push(
                queryClient.invalidateQueries({
                    queryKey: [QUERY_KEYS.media, 'broadcasts', id],
                    exact: false,
                    refetchActive: true,
                    refetchInactive: false,
                })
            );
        });
    }

    // Invalidate channels
    if (uniqueChannelIds.length > 0) {
        debugLog(`🔄 Invalidating ${uniqueChannelIds.length} specific channels`);

        uniqueChannelIds.forEach((id) => {
            if (!id) return;

            const invalidationKey = `channel:${id}`;
            trackInvalidation(invalidationKey);
            invalidationPromises.push(
                queryClient.invalidateQueries({
                    queryKey: [QUERY_KEYS.media, 'channels', id],
                    exact: false,
                    refetchActive: true,
                    refetchInactive: false,
                })
            );
        });
    }

    // Invalidate regular playlists
    if (uniquePlaylistIds.length > 0) {
        debugLog(`🔄 Invalidating ${uniquePlaylistIds.length} playlists`);

        uniquePlaylistIds.forEach((id) => {
            if (!id) return;

            const invalidationKey = `playlist:${id}`;
            trackInvalidation(invalidationKey);
            invalidationPromises.push(queryClient.invalidateQueries([QUERY_KEYS.playlists, id]));
        });
    }

    // Invalidate computed playlists (treating them the same way as regular playlists)
    if (uniqueComputedPlaylistIds.length > 0) {
        // Create a more descriptive log message that explains these are specifically from section.computedPlaylist
        debugLog(
            `🔄 Invalidating ${
                uniqueComputedPlaylistIds.length
            } section.computedPlaylist entries: ${uniqueComputedPlaylistIds.join(', ')}`
        );

        uniqueComputedPlaylistIds.forEach((id) => {
            if (!id) return;

            // Using the same invalidation approach as regular playlists but with different key prefix
            // This helps distinguish between regular playlist and computed playlist invalidations in logs
            const invalidationKey = `computed-playlist:${id}`;
            trackInvalidation(invalidationKey);
            invalidationPromises.push(queryClient.invalidateQueries([QUERY_KEYS.playlists, id]));
        });
    }

    // Clear all pending invalidations
    // eslint-disable-next-line no-param-reassign
    pendingAssetInvalidationsRef.current = [];
    pendingPlaylistInvalidationsRef.current.clear();
    pendingChannelInvalidationsRef.current.clear();

    // Wait for all invalidations to complete before fetching new data
    if (invalidationPromises.length > 0) {
        debugLog(`⏳ Awaiting ${invalidationPromises.length} invalidation operations`);
        await Promise.all(invalidationPromises);
        debugLog(`✅ All invalidations complete`);
    }
};

/**
 * Parameter interface for handleQuerySuccess function
 */
interface QuerySuccessParams {
    data: { pages?: PageSection[][] };
    lastDataRefreshTimeRef: React.MutableRefObject<number>;
    lastRefreshTimeRef: React.MutableRefObject<number>;
    lastDataHashRef: React.MutableRefObject<string>;
    lastCalculationTimeRef: React.MutableRefObject<number>;
    consecutiveUnchangedFetchesRef: React.MutableRefObject<number>;
    isFirstRender: React.MutableRefObject<boolean>;
}

/**
 * Updates tracking metrics when data is unchanged
 */
const handleUnchangedData = (
    consecutiveUnchangedFetchesRef: React.MutableRefObject<number>
): void => {
    // Increment the counter
    // eslint-disable-next-line no-param-reassign
    consecutiveUnchangedFetchesRef.current += 1;

    if (
        consecutiveUnchangedFetchesRef.current > 1 &&
        consecutiveUnchangedFetchesRef.current % CONSECUTIVE_FETCH_LOG_INTERVAL === 0
    ) {
        debugLog(
            `ℹ️ Data unchanged after ${consecutiveUnchangedFetchesRef.current} consecutive fetches`
        );
    }
};

/**
 * Handles successful query responses by updating tracking metrics
 * Updates data refresh timestamps, tracks data changes, and manages consecutive unchanged fetches
 * @param params Object containing all necessary parameters
 */
export const handleQuerySuccess = (params: QuerySuccessParams): void => {
    const {
        data,
        lastDataRefreshTimeRef,
        lastRefreshTimeRef,
        lastDataHashRef,
        lastCalculationTimeRef,
        consecutiveUnchangedFetchesRef,
        isFirstRender,
    } = params;

    const now = Date.now();

    // Update refresh time references
    // eslint-disable-next-line no-param-reassign
    lastDataRefreshTimeRef.current = now;
    // eslint-disable-next-line no-param-reassign
    lastRefreshTimeRef.current = now;

    // Track if data has changed
    const currentDataHash = generateDataHash(data);
    const dataHasChanged = currentDataHash !== lastDataHashRef.current;

    // Update hash for next comparison
    // eslint-disable-next-line no-param-reassign
    lastDataHashRef.current = currentDataHash;

    // Track consecutive unchanged fetches for potential backoff
    if (!dataHasChanged && !isFirstRender.current) {
        handleUnchangedData(consecutiveUnchangedFetchesRef);
    } else {
        // Reset the counter
        // eslint-disable-next-line no-param-reassign
        consecutiveUnchangedFetchesRef.current = 0;
        if (dataHasChanged) {
            debugLog(`📊 Data changed - resetting tracking metrics`);
        }
    }

    // Only log occasionally to reduce noise
    const timeSinceLastLog = now - lastCalculationTimeRef.current;
    if ((DEBUG && timeSinceLastLog > REFRESH_LOG_MIN_INTERVAL_MS) || dataHasChanged) {
        debugLog(
            `✅ Data refreshed at ${new Date(now).toLocaleTimeString('en-US', { hour12: false })}`
        );
    }

    // Reset first render flag
    if (isFirstRender.current) {
        // eslint-disable-next-line no-param-reassign
        isFirstRender.current = false;
    }
};

/**
 * Processes timestamps from data and handles calculation throttling
 * @returns Object containing timestamps and early return flags
 */
export const processTimestamps = (params: ProcessTimestampsParams): ProcessTimestampsResult => {
    const {
        data,
        calculationCountRef,
        lastCalculationTimeRef,
        prevTimestampsRef,
        backoffCountRef,
        lastDataHashRef,
        currentDataHash,
        lastInterval,
    } = params;
    // Check for calculation throttling
    if (calculationCountRef.current > MAX_TIMESTAMP_CALCULATIONS_PER_INTERVAL) {
        debugLog(
            `⚠️ Throttling timestamp calculations (${calculationCountRef.current} > ${MAX_TIMESTAMP_CALCULATIONS_PER_INTERVAL})`
        );
        return {
            timestamps: {} as TimestampRecord, // Empty timestamps since we're throttling
            shouldReturnEarly: true,
            returnValue: lastInterval || DEFAULT_REFETCH_INTERVAL, // Use previous interval or default
        };
    }

    // Extract timestamps from the data
    const lowestTimestamps = findLowestTimestamps(
        data,
        calculationCountRef,
        lastCalculationTimeRef
    );

    // Store results for future reference
    // eslint-disable-next-line no-param-reassign
    prevTimestampsRef.current = lowestTimestamps;
    // eslint-disable-next-line no-param-reassign
    lastDataHashRef.current = currentDataHash;

    // Log found timestamps for debugging
    if (DEBUG) {
        if (lowestTimestamps.past !== undefined) {
            const pastDate = new Date(lowestTimestamps.past * 1000);
            debugLog(
                `🕒 Found past timestamp: ${pastDate.toLocaleTimeString('en-US', {
                    hour12: false,
                })} (${msToTime(Date.now() - pastDate.getTime())} ago)`
            );
        }
        if (lowestTimestamps.future !== undefined) {
            const futureDate = new Date(lowestTimestamps.future * 1000);
            debugLog(
                `🕒 Found future timestamp: ${futureDate.toLocaleTimeString('en-US', {
                    hour12: false,
                })} (in ${msToTime(futureDate.getTime() - Date.now())})`
            );
        }
    }

    const hasItemsWithTimestamps =
        lowestTimestamps.past !== undefined || lowestTimestamps.future !== undefined;

    // If no items with timestamps, disable refetch and reset backoff
    if (!hasItemsWithTimestamps) {
        if (backoffCountRef.current > 0) {
            debugLog('⏱️ Resetting backoff counter (no timestamps found)');
        }
        // eslint-disable-next-line no-param-reassign
        backoffCountRef.current = 0; // Reset backoff counter
        debugLog('🛑 No timestamps found - disabling refetch');

        return {
            timestamps: lowestTimestamps,
            shouldReturnEarly: true,
            returnValue: false,
        };
    }

    // Continue processing with the timestamps
    return {
        timestamps: lowestTimestamps,
        shouldReturnEarly: false,
        returnValue: false,
    };
};

/**
 * Parameters for the trackDataChanges function
 */
interface TrackDataChangesParams {
    data: { pages?: PageSection[][] };
    prevTimestamps: TimestampRecord | null;
    lastInterval: number | false;
    lastDataHashRef: React.MutableRefObject<string>;
    lastCalculationTimeRef: React.MutableRefObject<number>;
    consecutiveUnchangedFetchesRef: React.MutableRefObject<number>;
    isFirstRender: boolean;
    targetTimeRef?: React.MutableRefObject<number | null>;
}

/**
 * Updates the consecutive unchanged fetches counter based on data changes
 */
const updateUnchangedFetchesCounter = (
    dataHasChanged: boolean,
    isFirstRender: boolean,
    consecutiveUnchangedFetchesRef: React.MutableRefObject<number>
): void => {
    if (!dataHasChanged && !isFirstRender) {
        // Increment the counter
        // eslint-disable-next-line no-param-reassign
        consecutiveUnchangedFetchesRef.current += 1;

        if (
            consecutiveUnchangedFetchesRef.current > 0 &&
            consecutiveUnchangedFetchesRef.current % CONSECUTIVE_FETCH_LOG_INTERVAL === 0
        ) {
            debugLog(
                `ℹ️ Data unchanged after ${consecutiveUnchangedFetchesRef.current} consecutive fetches`
            );
        }
    } else if (dataHasChanged) {
        // Reset the counter if data has changed
        // eslint-disable-next-line no-param-reassign
        consecutiveUnchangedFetchesRef.current = 0;
        debugLog(
            `✅ Data refreshed at ${new Date().toLocaleTimeString('en-US', { hour12: false })}`
        );
    }
};

/**
 * Checks if the interval can be reused based on target time or regular conditions
 */
const checkCanReuseInterval = (
    sameData: boolean,
    hasTimestamps: boolean,
    hasInterval: boolean,
    hasTargetTime: boolean,
    targetTimeStillFuture: boolean,
    recentCalculation: boolean
): boolean => {
    // For target time-based scheduling, reuse if target time is still in the future
    const canReuseBasedOnTargetTime = hasTargetTime && targetTimeStillFuture;

    // For regular interval-based scheduling
    const canReuseRegularInterval = sameData && hasTimestamps && hasInterval && recentCalculation;

    // Prioritize target time if available
    return hasTargetTime ? canReuseBasedOnTargetTime : canReuseRegularInterval;
};

/**
 * Logs debug information about why the interval can't be reused
 */
const logIntervalReuseDebugInfo = (params: IntervalReuseDebugInfo): void => {
    const {
        canReuseLastInterval,
        sameData,
        hasTimestamps,
        hasInterval,
        hasTargetTime,
        targetTimeStillFuture,
        recentCalculation,
        targetTimeRef,
        timeSinceLastCalculation,
        now,
    } = params;
    if (!DEBUG || canReuseLastInterval || !sameData) return;

    // Only log if data hasn't changed but we still can't reuse the interval
    const reasons: string[] = [];
    if (!hasTimestamps) reasons.push('no previous timestamps');
    if (!hasInterval) reasons.push('no previous interval');

    // If we have a target time, explain why we couldn't reuse based on that
    if (hasTargetTime) {
        if (!targetTimeStillFuture && targetTimeRef?.current) {
            const targetTime = new Date(targetTimeRef.current).toLocaleTimeString('en-US', {
                hour12: false,
            });
            reasons.push(`target time passed (${targetTime})`);
            debugLog(
                `🔍 [${now}] Target time based refetch - Cannot reuse interval because: ${reasons.join(
                    ', '
                )}`
            );

            const timeToTarget = targetTimeRef.current - Date.now();
            debugLog(
                `🎯 [${now}] Target time: ${targetTime} (${msToTime(Math.abs(timeToTarget))} ago)`
            );
        }
    } else {
        // Regular interval-based reuse failed
        // Provide more details about the calculation age
        const calculationAge = msToTime(timeSinceLastCalculation);
        const maxAge = msToTime(INTERVAL_REUSE_MAX_AGE);

        if (!recentCalculation) {
            reasons.push(`calculation too old (${calculationAge}, limit: ${maxAge})`);
        }

        if (reasons.length > 0) {
            debugLog(
                `🔍 [${now}] Regular interval - Cannot reuse interval because: ${reasons.join(
                    ', '
                )}`
            );
        }
    }
};

/**
 * Logs information about interval reuse when it's successful
 */
const logSuccessfulIntervalReuse = (
    canReuseLastInterval: boolean,
    timeSinceLastCalculation: number,
    hasTargetTime: boolean,
    targetTimeRef: React.MutableRefObject<number | null> | undefined,
    lastInterval: number | false,
    lastCalculationTimeRef: React.MutableRefObject<number>,
    now: number
): void => {
    if (!DEBUG || !canReuseLastInterval || timeSinceLastCalculation <= REUSE_LOG_MIN_INTERVAL_MS)
        return;

    const currentTime = new Date().toLocaleTimeString('en-US', { hour12: false });

    if (hasTargetTime && targetTimeRef && targetTimeRef.current !== null) {
        const timeLeft = targetTimeRef.current - now;
        const targetDate = new Date(targetTimeRef.current);

        debugLog(
            `♻️ [${currentTime}] Data unchanged - reusing previous calculation, next refetch in ${msToTime(
                timeLeft
            )} at ${targetDate.toLocaleTimeString('en-US', {
                hour12: false,
            })}`
        );

        // Add information about when the calculation was originally made
        const calculationTime = new Date(lastCalculationTimeRef.current).toLocaleTimeString(
            'en-US',
            { hour12: false }
        );
        const calculationAge = msToTime(timeSinceLastCalculation);
        debugLog(
            `ℹ️ [${currentTime}] Original calculation was made ${calculationAge} ago at ${calculationTime}`
        );
    } else {
        debugLog(
            `♻️ [${currentTime}] Data unchanged - reusing previous calculation, next refetch in:`,
            typeof lastInterval === 'number' ? msToTime(lastInterval) : 'not scheduled'
        );
    }
};

/**
 * Tracks data changes between fetches and manages the consecutive unchanged fetch counter
 * Determines whether we can reuse the previous calculation interval to optimize performance
 *
 * @param params - Parameters for the trackDataChanges function
 * @returns Object containing interval reuse flag and current data hash
 */
export const trackDataChanges = (params: TrackDataChangesParams): TrackDataChangesResult => {
    const {
        data,
        prevTimestamps,
        lastInterval,
        lastDataHashRef,
        lastCalculationTimeRef,
        consecutiveUnchangedFetchesRef,
        isFirstRender,
        targetTimeRef,
    } = params;

    // Generate a hash of the current data for comparison
    const currentDataHash = generateDataHash(data);
    const dataHasChanged = currentDataHash !== lastDataHashRef.current;
    const now = Date.now();
    const timeSinceLastCalculation = now - lastCalculationTimeRef.current;

    // Log data change status
    if (dataHasChanged && !isFirstRender) {
        debugLog('📊 Data has changed since last refetch calculation');
    }

    // Update consecutive unchanged fetches counter
    updateUnchangedFetchesCounter(dataHasChanged, isFirstRender, consecutiveUnchangedFetchesRef);

    // Check conditions for reusing the interval
    const sameData = !dataHasChanged;
    const hasTimestamps = prevTimestamps !== null;
    const hasInterval = lastInterval !== false;

    // Check if target time is still valid
    const hasTargetTime = Boolean(targetTimeRef && targetTimeRef.current !== null);
    const targetTimeStillFuture =
        hasTargetTime &&
        targetTimeRef &&
        targetTimeRef.current !== null &&
        targetTimeRef.current > now;

    // Log target time information for debugging
    if (hasTargetTime && DEBUG && targetTimeRef && targetTimeRef.current !== null) {
        const timeToTarget = targetTimeRef.current - now;

        if (timeToTarget <= 2000 && timeToTarget > 0) {
            debugLog(`⏰ Target time is approaching (${timeToTarget}ms away)`);
        } else if (timeToTarget < 0 && timeToTarget > -5000) {
            debugLog(`ℹ️ Target time just passed (${Math.abs(timeToTarget)}ms ago)`);
        }
    }

    // For regular interval-based scheduling
    const recentCalculation = timeSinceLastCalculation < INTERVAL_REUSE_MAX_AGE;

    // Check if we can reuse the interval
    const canReuseLastInterval = checkCanReuseInterval(
        sameData,
        hasTimestamps,
        hasInterval,
        hasTargetTime,
        Boolean(targetTimeStillFuture),
        recentCalculation
    );

    // Log debug information
    logIntervalReuseDebugInfo({
        canReuseLastInterval,
        sameData,
        hasTimestamps,
        hasInterval,
        hasTargetTime,
        targetTimeStillFuture: Boolean(targetTimeStillFuture),
        recentCalculation,
        targetTimeRef,
        timeSinceLastCalculation,
        now,
    });

    // Log successful interval reuse
    logSuccessfulIntervalReuse(
        canReuseLastInterval,
        timeSinceLastCalculation,
        hasTargetTime,
        targetTimeRef,
        lastInterval,
        lastCalculationTimeRef,
        now
    );

    return { canReuseLastInterval: !!canReuseLastInterval, currentDataHash };
};
